// What a deal's own clauses say about the deal.
//
// A module rather than two inline predicates, because both answers decide
// whether a WARNING is honest, and a warning that overstates is worse than no
// warning at all: a reader who checks one and finds it wrong stops reading the
// rest.

import type { TermLine } from "@crm/shared";

/**
 * Whether this deal still needs a partner agreement to price itself.
 *
 * A clause carrying `share_override_pct` states its own split, so it prices
 * with no agreement on file. The Contract Callers deals are all of that shape:
 * every clause says 100% ours, every report prices them, and the screen still
 * said "every report touching this deal fails with split_unresolvable".
 *
 *   needs_agreement  at least one active clause states no split, so the
 *                    agreement is what would resolve it — and there is none.
 *   self_stated      every active clause states its own split. Nothing is
 *                    broken; there is simply no agreement on file.
 *   no_clauses       nothing bills yet, so nothing is refused yet either.
 */
export type SplitCoverage = "needs_agreement" | "self_stated" | "no_clauses";

export function splitCoverage(lines: readonly TermLine[]): SplitCoverage {
  const active = lines.filter((line) => line.active);
  if (active.length === 0) return "no_clauses";
  return active.some((line) => line.share_override_pct === null)
    ? "needs_agreement"
    : "self_stated";
}

/**
 * The same answer for a page of deals, from one read of their clauses.
 *
 * A deal the read returned no clause for is `no_clauses`, which is what it is:
 * the request asked for exactly these deals, so an absent deal has none.
 * Nothing at all until the clauses land — the caller passes `undefined` while
 * the read is out, and an empty map means "not known yet", not "no clauses".
 */
export function splitCoverageByDeal(
  dealIds: readonly string[],
  lines: readonly TermLine[] | undefined,
): ReadonlyMap<string, SplitCoverage> {
  if (lines === undefined) return new Map();
  const byDeal = new Map<string, TermLine[]>(dealIds.map((id) => [id, []]));
  for (const line of lines) byDeal.get(line.deal_id)?.push(line);
  return new Map([...byDeal].map(([id, own]) => [id, splitCoverage(own)]));
}

/**
 * Whether a clause bills as its milestones land — the shape an implementation
 * fee takes. A retired clause is not counted: nobody bills it.
 */
export function hasMilestoneClause(lines: readonly TermLine[]): boolean {
  return lines.some((line) => line.active && line.cadence === "milestone");
}
